import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });
      await fetch("https://readdy.ai/api/form/d79pjfr86jhav3jpfoog", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div className="mb-16">
          <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase mb-4">/ Contact</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            Get In Touch
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Info */}
          <div className="lg:w-2/5 flex flex-col gap-8">
            <div className="bg-gray-900 rounded-3xl p-8 text-white">
              <h3 className="text-lg font-bold mb-6 text-white/80 uppercase tracking-widest text-xs">
                Company Information
              </h3>

              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className="ri-map-pin-line text-emerald-400 text-base" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Address</p>
                    <p className="text-white text-sm font-medium">OMLADINSKA S-3</p>
                    <p className="text-white/70 text-sm">City Becej</p>
                    <p className="text-white/70 text-sm">Zip 380706</p>
                    <p className="text-white/70 text-sm">Country Serbia</p>
                  </div>
                </div>

                <div className="w-full h-px bg-white/10" />

                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className="ri-mail-line text-emerald-400 text-base" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Email</p>
                    <p className="text-white text-sm font-medium">contact@bazadoobecej.com</p>
                  </div>
                </div>

                <div className="w-full h-px bg-white/10" />

                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className="ri-phone-line text-emerald-400 text-base" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-widest mb-1">Phone Number</p>
                    <p className="text-white text-sm font-medium">+1 7405549511</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Company name watermark card */}
            <div className="rounded-3xl bg-[#F7F6F2] p-8 flex items-center justify-center">
              <div className="text-center">
                <img
                  src="https://public.readdy.ai/ai/img_res/dcb3dcf6-6b60-485e-94ca-230cc19e0552.png"
                  alt="BAZA DOO BECEJ Logo"
                  className="w-16 h-16 object-contain mx-auto mb-4"
                />
                <p className="text-gray-900 font-extrabold text-lg tracking-wide">BAZA DOO BECEJ</p>
                <p className="text-gray-400 text-xs mt-1">iOS Application Development</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-3/5">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20 bg-[#F7F6F2] rounded-3xl">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mb-6">
                  <i className="ri-check-line text-emerald-600 text-2xl" />
                </div>
                <h3 className="text-gray-900 font-bold text-xl mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm max-w-xs">
                  Thank you for reaching out. Our team will get back to you shortly.
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col sm:flex-row gap-5">
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#F7F6F2] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-[#F7F6F2] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="Tell us about your inquiry..."
                    value={formData.message}
                    onChange={(e) => {
                      setFormData({ ...formData, message: e.target.value });
                      setCharCount(e.target.value.length);
                    }}
                    className="bg-[#F7F6F2] border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-colors resize-none"
                  />
                  <p className={`text-xs text-right ${charCount > 480 ? "text-red-400" : "text-gray-400"}`}>
                    {charCount}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="bg-gray-900 text-white rounded-xl px-8 py-4 text-sm font-semibold tracking-wide hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed self-start"
                >
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer bottom */}
        <div className="mt-20 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-400 text-xs">
            &copy; {new Date().getFullYear()} BAZA DOO BECEJ. All rights reserved.
          </p>
          <p className="text-gray-400 text-xs">iOS Application Development &mdash; Serbia</p>
        </div>
      </div>
    </section>
  );
}
